import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, of, throwError } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import {
    ClientManagerInstanceToken,
    ClientManagerInterface,
    SharpServiceNames,
} from '../../interfaces/client-manager.interface';
import { UserMasterInterface } from '../../interfaces/user-master.interface';
import { CompositeModel } from '../../models/complex/composite.model';
import { UserModel, UserModelFromJson } from '../../models/complex/user.model';
import { TextModel } from '../../models/primitive/text.model';
import { LoadStatus } from '../../state/load-status';
import { initUserMaster } from '../../state/user-master/actions/user-master.actions';
import {
    selectSelectedUser,
    selectUserMasterLoaded,
    selectUserMasterStatus,
    selectUserRole,
} from '../../state/user-master/selectors/user-master.selector';
import { User } from '../../types/user';
import { InitError } from '../base-initialization/init-error';
import { UriUtilitiesService } from '../uri-utilities/uri-utilities.service';

const USERMODEL_NOT_SELECTED_MESSAGE = `User model not selected yet`;

/**
 * NgRx-backed implementation of {@link UserMasterInterface}. The selected sales-org/plant
 * user lives in the user-master store slice, resolved by {@link UserMasterEffects}; every
 * getter reads it through selectors. `init()` triggers the resolution effect and bridges the
 * slice status back into the imperative `InitError` contract the `BaseInitializationService`
 * pipeline expects.
 */
@Injectable({
    providedIn: 'root',
})
export class UserMasterNgrxService implements UserMasterInterface {
    public defaultSharpServiceText = SharpServiceNames.PACE_PWA;

    public constructor(
        private readonly store: Store,
        private readonly uriUtilities: UriUtilitiesService,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
    ) {}

    public get isInitialized(): boolean {
        return this.store.selectSignal(selectUserMasterLoaded)();
    }

    /**
     * Trigger the resolution effect and resolve once the slice settles: complete on success,
     * or error with a hard-failing {@link InitError} on failure (matching the legacy policy —
     * every user-master init failure aborts startup).
     */
    public init(): Observable<void> {
        this.store.dispatch(initUserMaster());
        return this.store.select(selectUserMasterStatus).pipe(
            filter((status) => status === LoadStatus.Loaded || status === LoadStatus.Error),
            take(1),
            switchMap((status) =>
                status === LoadStatus.Error
                    ? throwError(() => new InitError(new Error('UserMasterNgrxService: init failed'), true))
                    : of(undefined as void),
            ),
        );
    }

    public getSelectedUserModel(): UserModel {
        return new UserModelFromJson(this.requireSelectedUser());
    }

    public getSelectedUser(): User {
        return this.requireSelectedUser();
    }

    public getUserRole(): Observable<string> {
        return of(this.store.selectSignal(selectUserRole)());
    }

    public setSalesOrgAndPlantForModel(model: CompositeModel): void {
        const selectedUser = this.getSelectedUserModel();
        model.set(new TextModel('SalesOrg', selectedUser.getSalesOrg()));
        model.set(new TextModel('Plant', selectedUser.getPlant()));
    }

    public setSalesOrgAndPlantForUrl(query: string): string {
        return this.uriUtilities.appendUriFilters(query, this.getSelectedUserModel());
    }

    // Determine if the user is external or internal, i.e. whether PACE or PACE Online is used.
    public isPaceOnlineUser(): boolean {
        if (!this.isInitialized) {
            return true;
        }
        const frontId = this.store.selectSignal(selectSelectedUser)()?.frontId;
        return !(frontId && frontId[0] === 'Z');
    }

    public getSharpServiceText(): SharpServiceNames {
        if (!this.isInitialized) {
            throw new Error(
                `UserMasterNgrxService: getSharpServiceText: Trying to call UserMasterService before initialization!`,
            );
        }
        if (this.clientManager.isPaceFormsFI()) {
            return SharpServiceNames.PACE_FORMS_FI;
        } else if (this.clientManager.isNgmP58()) {
            if (this.isPaceOnlineUser()) {
                return SharpServiceNames.PACE_ONLINE;
            }
            return SharpServiceNames.PACE_PWA;
        } else {
            // if no match occurs return PACE text as default
            return this.defaultSharpServiceText;
        }
    }

    /**
     * Read the selected user from the slice, mirroring the legacy guards: fail if the service
     * has not been initialized, or if no user has been selected.
     *
     * @throws if not initialized or no user selected.
     */
    private requireSelectedUser(): User {
        if (!this.isInitialized) {
            throw new Error(
                `UserMasterNgrxService: getSelectedUserModel: Trying to call UserMasterService before initialization!`,
            );
        }
        const selectedUser = this.store.selectSignal(selectSelectedUser)();
        if (!selectedUser) {
            throw new Error(USERMODEL_NOT_SELECTED_MESSAGE);
        }
        return selectedUser;
    }
}
